import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { ApiResponse } from '../models/api-response.models';
import { SellerBusiness } from '../models/product.models';
import { Business, BusinessType } from '../models/user.models';
import { unwrapApiResponse } from '../utils/api-response';
import { ApiService } from './api.service';
import { TokenService } from '../auth/token.service';

export interface OnboardBusinessRequest {
  businessTypeId: string;
  legalName: string;
  tradingName: string;
  registrationNumber: string;
  taxNumber: string;
  email: string;
  phone: string;
  website?: string;
  logoUrl?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  documents: Array<{
    documentType: string;
    documentName: string;
    documentNumber: string;
    fileUrl: string;
    issuedAt: string | null;
    expiresAt: string | null;
  }>;
  bankDetail: {
    bankName: string;
    branchName: string;
    accountHolderName: string;
    accountNumber: string;
    routingNumber: string | null;
    swiftCode: string | null;
    iban: string | null;
    currency: string;
    isPrimary: boolean;
  };
}

export interface BusinessStore {
  id: string;
  businessId?: string;
  tenantId?: string;
  name: string;
  code?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  area?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postalCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  phone?: string | null;
  email?: string | null;
  isPrimary?: boolean;
  isActive?: boolean;
  createdAt?: string;
}

export interface CreateBusinessStoreRequest {
  name: string;
  code?: string;
  addressLine1?: string;
  addressLine2?: string;
  area?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  phone?: string;
  email?: string;
  isPrimary?: boolean;
}

export interface BusinessSubscription {
  plan: string;
  status: string;
  maxStores: number;
  activeStores: number;
  canCreateStore: boolean;
}

export type BusinessLocationScope = 'business' | 'platform';

export interface CreateInternalBusinessRequest {
  legalName: string; tradingName: string; registrationNumber: string; taxNumber: string;
  ownerFirstName: string; ownerLastName: string; ownerEmail: string; phone: string;
  temporaryPassword: string; website: string; addressLine1: string; addressLine2: string;
  city: string; state: string; country: string; postalCode: string; locationName: string;
  latitude: number | null; longitude: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class BusinessService {
  private readonly api = inject(ApiService);
  private readonly tokens = inject(TokenService);
  private readonly locationCache = new Map<string, BusinessStore[]>();
  private businessesCache$?: Observable<SellerBusiness[]>;
  private businessesCachedAt = 0;

  types(): Observable<BusinessType[]> {
    return this.api
      .get<
        | ApiResponse<BusinessType[] | { businessTypes: BusinessType[] }>
        | BusinessType[]
        | { businessTypes: BusinessType[] }
      >(API_ENDPOINTS.business.types, undefined, 'business', 15_000)
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => (Array.isArray(response) ? response : (response.businessTypes ?? []))),
      );
  }

  onboard(payload: OnboardBusinessRequest): Observable<Business> {
    return this.api
      .post<
        ApiResponse<Business | { business: Business }> | Business | { business: Business },
        OnboardBusinessRequest
      >(API_ENDPOINTS.business.onboard, payload, 'business')
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => ('business' in response ? response.business : response)),
      );
  }

  onboardWithDocuments(payload: FormData): Observable<Business> {
    return this.api
      .post<ApiResponse<Business | { business: Business }> | Business | { business: Business }, FormData>(
        API_ENDPOINTS.business.onboardWithDocuments, payload, 'business', 90_000)
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => ('business' in response ? response.business : response)),
      );
  }

  adminProfileByTenant(tenantId: string): Observable<Business | null> {
    return this.api
      .get<ApiResponse<Business | { business: Business | null }> | Business | { business: Business | null }>(
        API_ENDPOINTS.business.adminByTenant(tenantId), undefined, 'business')
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => (response && 'business' in response ? response.business : response)),
      );
  }

  mine(): Observable<Business | null> {
    return this.api
      .get<
        | ApiResponse<Business | { business: Business | null }>
        | Business
        | { business: Business | null }
      >(API_ENDPOINTS.business.me, undefined, 'business', 15_000)
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => (response && 'business' in response ? response.business : response)),
      );
  }

  businesses(forceRefresh = false): Observable<SellerBusiness[]> {
    const cacheIsFresh = Date.now() - this.businessesCachedAt < 15_000;
    if (!forceRefresh && cacheIsFresh && this.businessesCache$) return this.businessesCache$;

    this.businessesCachedAt = Date.now();
    this.businessesCache$ = this.api
      .get<ApiResponse<{ tenants: SellerBusiness[] }> | { tenants: SellerBusiness[] }>(
        API_ENDPOINTS.tenants.base,
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => response.tenants ?? []),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    return this.businessesCache$;
  }

  createInternalBusiness(payload: CreateInternalBusinessRequest): Observable<{ tenantId: string; ownerUserId: string; locationId: string; plan: string; status: string }> {
    return this.api.post<
      ApiResponse<{ tenantId: string; ownerUserId: string; locationId: string; plan: string; status: string }> |
      { tenantId: string; ownerUserId: string; locationId: string; plan: string; status: string },
      CreateInternalBusinessRequest
    >(API_ENDPOINTS.tenants.internalBusinesses, payload).pipe(map((response) => unwrapApiResponse(response)));
  }

  entitlementPermissionIds(tenantId: string): Observable<string[]> {
    return this.api
      .get<ApiResponse<{ permissionIds?: string[] }> | { permissionIds?: string[] }>(
        API_ENDPOINTS.tenants.entitlements(tenantId),
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => response.permissionIds ?? []),
      );
  }

  replaceEntitlements(tenantId: string, permissionIds: string[]): Observable<string[]> {
    return this.api
      .put<
        ApiResponse<{ permissionIds?: string[] }> | { permissionIds?: string[] },
        { permissionIds: string[] }
      >(API_ENDPOINTS.tenants.entitlements(tenantId), { permissionIds })
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => response.permissionIds ?? permissionIds),
      );
  }

  reviewTenant(
    tenantId: string,
    status: 'Pending' | 'Approved' | 'CorrectionRequested' | 'OnHold' | 'Rejected' | 'Blacklisted',
    reason: string,
  ): Observable<unknown> {
    return this.api.put(API_ENDPOINTS.tenants.approval(tenantId), { status, reason });
  }

  myApprovalStatus(): Observable<{
    approvalStatus: string;
    approvalReason?: string | null;
    phone?: string | null;
    businessKind?: string | null;
  }> {
    return this.api
      .get<
        | ApiResponse<{ business: { approvalStatus: string; approvalReason?: string | null; phone?: string | null; businessKind?: string | null } }>
        | {
            business: { approvalStatus: string; approvalReason?: string | null; phone?: string | null; businessKind?: string | null };
          }
      >(API_ENDPOINTS.tenants.myStatus)
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => response.business),
      );
  }

  resubmitMine(): Observable<{ approvalStatus: string; resubmitted: boolean }> {
    return this.api.post<ApiResponse<{ approvalStatus: string; resubmitted: boolean }> | { approvalStatus: string; resubmitted: boolean }, Record<string, never>>(
      API_ENDPOINTS.tenants.resubmitMine, {}, 'identity', 15_000,
    ).pipe(map((response) => unwrapApiResponse(response)));
  }

  tenantStores(tenantId: string): Observable<BusinessStore[]> {
    return this.api
      .get<
        | ApiResponse<BusinessStore[] | { locations: BusinessStore[] }>
        | BusinessStore[]
        | { locations: BusinessStore[] }
      >(API_ENDPOINTS.business.tenantLocations(tenantId), { includeInactive: false }, 'business')
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => (Array.isArray(response) ? response : (response.locations ?? []))),
      );
  }

  stores(
    scope: BusinessLocationScope = 'business',
    forceRefresh = false,
  ): Observable<BusinessStore[]> {
    const cacheKey = this.locationCacheKey(scope);
    const cachedLocations = this.locationCache.get(cacheKey);
    if (!forceRefresh && cachedLocations) {
      return of(cachedLocations);
    }

    const path = this.locationPath(scope);
    return this.api
      .get<
        | ApiResponse<BusinessStore[] | { locations: BusinessStore[] }>
        | BusinessStore[]
        | { locations: BusinessStore[] }
      >(path, { includeInactive: false }, 'business')
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => (Array.isArray(response) ? response : (response.locations ?? []))),
        tap((locations) => this.locationCache.set(cacheKey, locations)),
      );
  }

  createStore(
    payload: CreateBusinessStoreRequest,
    scope: BusinessLocationScope = 'business',
  ): Observable<BusinessStore> {
    return this.api
      .post<
        | ApiResponse<BusinessStore | { location: BusinessStore }>
        | BusinessStore
        | { location: BusinessStore },
        CreateBusinessStoreRequest
      >(this.locationPath(scope), payload, 'business')
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => ('location' in response ? response.location : response)),
        tap((location) => {
          const cacheKey = this.locationCacheKey(scope);
          const locations = this.locationCache.get(cacheKey) ?? [];
          const existingLocations = locations
            .filter((item) => item.id !== location.id)
            .map((item) => (location.isPrimary ? { ...item, isPrimary: false } : item));
          this.locationCache.set(cacheKey, [location, ...existingLocations]);
        }),
      );
  }

  setPrimaryStore(
    storeId: string,
    scope: BusinessLocationScope = 'business',
  ): Observable<BusinessStore> {
    return this.api
      .patch<
        | ApiResponse<BusinessStore | { location: BusinessStore }>
        | BusinessStore
        | { location: BusinessStore },
        Record<string, never>
      >(`${this.locationPath(scope)}/${storeId}/primary`, {}, 'business')
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => ('location' in response ? response.location : response)),
        tap((location) => {
          const cacheKey = this.locationCacheKey(scope);
          const locations = this.locationCache.get(cacheKey) ?? [];
          this.locationCache.set(
            cacheKey,
            locations.map((item) => ({ ...item, isPrimary: item.id === location.id })),
          );
        }),
      );
  }

  deactivateStore(storeId: string, scope: BusinessLocationScope = 'business'): Observable<void> {
    return this.api
      .delete<ApiResponse<unknown> | unknown>(`${this.locationPath(scope)}/${storeId}`, 'business')
      .pipe(
        tap(() => {
          const cacheKey = this.locationCacheKey(scope);
          const locations = this.locationCache.get(cacheKey) ?? [];
          this.locationCache.set(
            cacheKey,
            locations.filter((location) => location.id !== storeId),
          );
        }),
        map(() => undefined),
      );
  }

  subscription(): Observable<BusinessSubscription> {
    return this.api
      .get<
        | ApiResponse<BusinessSubscription | { subscription: BusinessSubscription }>
        | BusinessSubscription
        | { subscription: BusinessSubscription }
      >(API_ENDPOINTS.business.subscription, undefined, 'business')
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => ('subscription' in response ? response.subscription : response)),
        map((subscription) => {
          const current = subscription as BusinessSubscription & {
            maxLocations?: number;
            activeLocations?: number;
            canCreateLocation?: boolean;
          };
          return {
            plan: current.plan,
            status: current.status,
            maxStores: current.maxStores ?? current.maxLocations ?? 0,
            activeStores: current.activeStores ?? current.activeLocations ?? 0,
            canCreateStore: current.canCreateStore ?? current.canCreateLocation ?? false,
          };
        }),
      );
  }

  private locationPath(scope: BusinessLocationScope): string {
    return scope === 'platform'
      ? API_ENDPOINTS.business.adminLocations
      : API_ENDPOINTS.business.locations;
  }

  private locationCacheKey(scope: BusinessLocationScope): string {
    return scope === 'platform' ? scope : `${scope}:${this.tokens.getTenantId() ?? 'unknown'}`;
  }
}
